import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatReviewVersionLabel } from "@/lib/review-display";
import type { ReviewVersionSummary } from "@/types";

interface ReviewVersionSelectorProps {
  currentReviewId: string;
  versions: ReviewVersionSummary[];
}

function versionDisplayLabel(version: ReviewVersionSummary, index: number): string {
  return (
    version.version ?? formatReviewVersionLabel(index + 1, version.created_at)
  );
}

export function ReviewVersionSelector({
  currentReviewId,
  versions,
}: ReviewVersionSelectorProps) {
  const navigate = useNavigate();

  if (versions.length <= 1) return null;

  const currentIndex = versions.findIndex((v) => v.id === currentReviewId);
  const currentVersion =
    currentIndex >= 0
      ? versions[currentIndex]
      : versions[versions.length - 1];
  const currentIndexForLabel = currentIndex >= 0 ? currentIndex : versions.length - 1;
  const current = versionDisplayLabel(currentVersion, currentIndexForLabel);

  return (
    <Select
      value={currentReviewId}
      onValueChange={(nextId) => {
        if (nextId !== currentReviewId) {
          navigate(`/contract-review/${nextId}`);
        }
      }}
    >
      <SelectTrigger className="h-9 w-[11rem] max-w-full border-[var(--lm-border-subtle)] bg-white text-sm dark:border-slate-700 dark:bg-slate-900/60">
        <SelectValue>{current}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {versions.map((version, index) => (
          <SelectItem key={version.id} value={version.id}>
            {versionDisplayLabel(version, index)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
